import { z } from 'zod';
import { RowVersionSchema } from '../common/row-version.js';
// Response schemas for the roles feature. Field-exposure rule (ANATOMY §L7).
// `user_count` is computed (count of user_project_roles referencing the role) —
// drives the list display and the ROLE_IN_USE disable guard's user feedback.
// ── GET /api/projects/:projectId/roles — list row ───────────────────────────
export const RoleListItemSchema = z
    .object({
    row_version: RowVersionSchema,
    id: z.number().int(),
    name: z.string().max(32),
    description: z.string().max(500).nullable(),
    inactivity_timeout_min: z.number().int().nullable(),
    is_disabled: z.boolean(),
    user_count: z.number().int().nonnegative(),
    // Stored-grant counts for the list's Capabilities + Delegates columns (raw
    // grants — what the admin granted; the decision-time entitlement filter
    // affects users' effective sets, not the role's stored count). The full key
    // list rides the detail (RoleDetail.permissions); the list shows only counts.
    permission_count: z.number().int().nonnegative(),
    managed_role_count: z.number().int().nonnegative(),
})
    .strict();
export const RolesListResponseSchema = z
    .object({ items: z.array(RoleListItemSchema) })
    .strict();
// ── GET /api/projects/:projectId/roles/:roleId — detail ─────────────────────
// Adds the permission grants + the managed-roles delegation set + the
// responsibility level-set (the role-edit form needs all three).
export const RoleDetailSchema = z
    .object({
    row_version: RowVersionSchema,
    id: z.number().int(),
    name: z.string().max(32),
    description: z.string().max(500).nullable(),
    inactivity_timeout_min: z.number().int().nullable(),
    is_disabled: z.boolean(),
    // The role's stored capability keys (raw grants — NOT includes-expanded; the
    // editor shows exactly what was granted). Tolerant `string` on read so the
    // response never fails serialization if the catalogue evolves; writes are
    // strict (validated against the grantable enum in requests.ts).
    permissions: z.array(z.string()),
    managed_roles: z.array(z.number().int()),
    // The inline responsibility level-set (ADR-026 §E — which levels a holder may
    // ASSIGN). Tolerant `string` on read (like permissions); writes are strict.
    // Enforcement rides APP-185 — stored + round-tripped now.
    responsibility_levels: z.array(z.string()),
    user_count: z.number().int().nonnegative(),
})
    .strict();
// ── POST create — minimal id (FE refetches list) ────────────────────────────
export const CreatedRoleResponseSchema = z
    .object({ id: z.number().int() })
    .strict();
